import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { ArrowLeft, ChevronRight, CheckCircle, Award, Star, TrendingUp, Shield, X, RefreshCw } from "lucide-react";

const QUALIFICATIONS = [
  "No Formal Qualification", "Graduation (Any)", "Graduation (Finance)",
  "Post Graduation (Any)", "Post Graduation (Finance)", "MBA",
  "Chartered Accountant", "Doctorate",
];

const PRODUCTS = [
  { id: "forward", label: "Forward Contract" },
  { id: "par_forward", label: "Par Forward" },
  { id: "vanilla_option", label: "Vanilla Call / Put Option" },
  { id: "multiple_options", label: "One or More Call / Put Options" },
  { id: "none", label: "None" },
];

const BANKS = ["HDFC Bank","ICICI Bank","SBI","Axis Bank","Kotak Mahindra","Yes Bank","Citibank","Standard Chartered","HSBC","Deutsche Bank","Barclays","BNP Paribas","Other"];

const GRADES = [
  {
    grade: "Novice", minScore: 0, maxScore: 3,
    color: "from-gray-500 to-gray-600", textColor: "text-gray-400",
    borderColor: "border-gray-500/30", bg: "bg-gray-500/10",
    icon: "🌱", stars: 1,
    message: "You are just getting started with FX hedging.",
    detail: "Based on your responses, you are at the beginning of your hedging journey. We recommend starting with simple Forward Contracts to protect your cash flows before exploring more complex instruments. Our team at Nebula Finance will guide you every step of the way.",
    recommendation: "Start with Forward Contracts only. Avoid derivatives until you have more experience.",
    products: ["Forward Contract"],
  },
  {
    grade: "Beginner", minScore: 4, maxScore: 6,
    color: "from-blue-500 to-cyan-600", textColor: "text-blue-400",
    borderColor: "border-blue-500/30", bg: "bg-blue-500/10",
    icon: "📈", stars: 2,
    message: "You have a basic understanding of FX hedging.",
    detail: "You have some exposure to hedging instruments and understand the basics. You are ready to explore Par Forwards and simple vanilla options, but we recommend getting proper guidance before using structured products.",
    recommendation: "Forward Contracts and Par Forwards are suitable. Vanilla options with guidance.",
    products: ["Forward Contract", "Par Forward", "Vanilla Call/Put Option"],
  },
  {
    grade: "Intermediate", minScore: 7, maxScore: 9,
    color: "from-amber-500 to-orange-500", textColor: "text-amber-400",
    borderColor: "border-amber-500/30", bg: "bg-amber-500/10",
    icon: "⚡", stars: 3,
    message: "You have a solid understanding of FX derivatives.",
    detail: "You clearly have experience with derivatives and understand both their potential and their risks. You are well-positioned to use structured products like Range Forwards and Seagulls.",
    recommendation: "Suitable for most hedging instruments including structured products with board approval.",
    products: ["Forward Contract", "Par Forward", "Vanilla Call/Put Option", "Range Forward", "Seagull"],
  },
  {
    grade: "Professional", minScore: 10, maxScore: 100,
    color: "from-indigo-500 to-purple-600", textColor: "text-indigo-400",
    borderColor: "border-indigo-500/30", bg: "bg-indigo-500/10",
    icon: "🏆", stars: 4,
    message: "Congratulations — you are a hedging professional!",
    detail: "You have demonstrated an excellent understanding of FX derivatives, a strong educational background in finance, and practical experience with complex hedging instruments.",
    recommendation: "Full suite of hedging instruments available including all structured products.",
    products: ["Forward Contract", "Par Forward", "Vanilla Call/Put Option", "Range Forward", "Seagull", "Participating Forward", "Window Forward"],
  },
];

function getGrade(score) {
  return GRADES.find(g => score >= g.minScore && score <= g.maxScore) || GRADES[0];
}

function calculateScore(answers) {
  let score = 0;
  if (answers.does_hedge) score += 2;
  const products = answers.products_used || [];
  if (products.includes("vanilla_option") || products.includes("multiple_options")) score += 2;
  if (products.includes("par_forward")) score += 1;
  const qualScores = {
    "No Formal Qualification": 0, "Graduation (Any)": 1,
    "Graduation (Finance)": 2, "Post Graduation (Any)": 2,
    "Post Graduation (Finance)": 3, "MBA": 3,
    "Chartered Accountant": 4, "Doctorate": 4,
  };
  score += qualScores[answers.qualification] || 0;
  if (answers.used_derivatives) score += 2;
  if (answers.lost_in_derivatives) score -= 1;
  if (answers.board_allows_derivatives) score += 2;
  return Math.max(0, score);
}

function formatDateInput(value) {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}-${digits.slice(2)}`;
  return `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4,8)}`;
}

function toISO(ddmmyyyy) {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  return `${yyyy}-${mm}-${dd}`;
}

function fromISO(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(isoDate);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function getVolatility(days) {
  if (!days || days <= 0) return 0;
  if (days <= 30)  return 5.2;
  if (days <= 60)  return 6.8;
  if (days <= 90)  return 7.5;
  if (days <= 180) return 8.9;
  if (days <= 270) return 10.2;
  return 11.5;
}

function getRecommendation(gradeObj, volatility, days, budgetedRate, spotRate) {
  const buffer = ((budgetedRate - spotRate) / spotRate) * 100;
  let product = gradeObj.products[0];
  let rationale = "";
  let indicativeRate = spotRate;

  if (gradeObj.grade === "Novice") {
    product = "Forward Contract";
    indicativeRate = spotRate * (1 + (volatility / 100) * 0.3);
    rationale = "Given your current experience level, a Forward Contract is the most appropriate instrument. It locks in a fixed rate eliminating uncertainty completely.";
  } else if (gradeObj.grade === "Beginner") {
    if (volatility > 8) {
      product = "Par Forward";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.25);
      rationale = "Given the elevated volatility, a Par Forward is recommended — it spreads your exposure across multiple dates at a single fixed rate, reducing timing risk.";
    } else {
      product = "Forward Contract";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.3);
      rationale = "A Forward Contract is the ideal fit — straightforward, effective and suitable for your current experience.";
    }
  } else if (gradeObj.grade === "Intermediate") {
    if (buffer > 3 && volatility > 7) {
      product = "Range Forward";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.2);
      rationale = "A Range Forward is recommended — it gives you protection on the downside while allowing participation in favourable moves, ideal given the current volatility and your budget buffer.";
    } else if (volatility > 9) {
      product = "Seagull";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.15);
      rationale = "High volatility makes a Seagull structure attractive — it provides cost-effective protection with a defined worst-case rate.";
    } else {
      product = "Par Forward";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.25);
      rationale = "A Par Forward offers structured protection across your exposure period at a competitive rate.";
    }
  } else {
    if (days > 180 && volatility > 9) {
      product = "Participating Forward";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.18);
      rationale = "For a longer dated exposure in a high-volatility environment, a Participating Forward allows you to benefit from favourable moves while ensuring downside protection.";
    } else if (volatility > 10) {
      product = "Seagull";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.12);
      rationale = "A Seagull structure is recommended — zero cost, defined floor and ceiling, well suited to your experience level and the current market conditions.";
    } else {
      product = "Range Forward";
      indicativeRate = spotRate * (1 + (volatility / 100) * 0.2);
      rationale = "A Range Forward provides an optimal balance of protection and participation, well matched to your risk profile.";
    }
  }
  return { product, indicativeRate: +indicativeRate.toFixed(4), rationale };
}

export default function HedgeSolutions({ onBack, companyId }) {
  const [page, setPage] = useState("loading");
  const [saving, setSaving] = useState(false);
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [gradeResult, setGradeResult] = useState(null);
  const [score, setScore] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [exposures, setExposures] = useState([]);
  const [loadingExposures, setLoadingExposures] = useState(true);
  const [fxRates, setFxRates] = useState({});
  const [hedgeRows, setHedgeRows] = useState([]);
  const [addHedgeRow, setAddHedgeRow] = useState(null);
  const [addHedgeForm, setAddHedgeForm] = useState({});
  const [addHedgeSaving, setAddHedgeSaving] = useState(false);

  const [answers, setAnswers] = useState({
    does_hedge: null, products_used: [],
    qualification: "", used_derivatives: null,
    lost_in_derivatives: null, board_allows_derivatives: null,
  });

  useEffect(() => {
    if (!companyId) return;
    
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('hedge_risk_assessments')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error("Error loading assessment:", error);
          setPage("intro");
          return;
        }

        if (data && data.length > 0) {
          setExistingAssessment(data[0]);
          const g = getGrade(data[0].score);
          setGradeResult(g);
          setScore(data[0].score);
          setPage("hedge");
        } else {
          setPage("intro");
        }
      } catch (e) {
        console.error(e);
        setPage("intro");
      }
    };
    load();
  }, [companyId]);

  const loadExposures = useCallback(async () => {
    if (!companyId) return;
    setLoadingExposures(true);
    try {
      const [recRes, payRes, rateRes] = await Promise.all([
        supabase.from('receivables').select('*').eq('company_id', companyId).order('due_date', { ascending: true }),
        supabase.from('payables').select('*').eq('company_id', companyId).order('due_date', { ascending: true }),
        fetch("https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/inr.json"),
      ]);

      const recData = recRes.data || [];
      const payData = payRes.data || [];
      const rateData = await rateRes.json();

      const inrRates = rateData?.inr || {};
      const rates = {};
      Object.keys(inrRates).forEach(k => {
        if (inrRates[k]) rates[k.toUpperCase()] = +(1 / inrRates[k]).toFixed(4);
      });
      setFxRates(rates);

      const ccyMap = {};
      recData
        .filter(r => r.status !== "Settled" && r.currency && r.currency !== "INR")
        .forEach(r => {
          const ccy = r.currency;
          if (!ccyMap[ccy]) ccyMap[ccy] = { rec: 0, pay: 0, recDates: [], payDates: [] };
          const amt = parseFloat((r.amount || "").toString().replace(/[^0-9.]/g, "")) || 0;
          const settled = parseFloat(r.settled_amount) || 0;
          ccyMap[ccy].rec += Math.max(0, amt - settled);
          if (r.due_date) ccyMap[ccy].recDates.push(r.due_date);
        });
      payData
        .filter(p => p.status !== "Settled" && p.currency && p.currency !== "INR")
        .forEach(p => {
          const ccy = p.currency;
          if (!ccyMap[ccy]) ccyMap[ccy] = { rec: 0, pay: 0, recDates: [], payDates: [] };
          const amt = parseFloat((p.amount || "").toString().replace(/[^0-9.]/g, "")) || 0;
          const settled = parseFloat(p.settled_amount) || 0;
          ccyMap[ccy].pay += Math.max(0, amt - settled);
          if (p.due_date) ccyMap[ccy].payDates.push(p.due_date);
        });

      const rows = Object.entries(ccyMap).map(([ccy, v]) => {
        const naturalHedge = Math.min(v.rec, v.pay);
        const netExposure = Math.abs(v.rec - v.pay);
        const direction = v.rec > v.pay ? "Receivable" : "Payable";
        const allDates = [...v.recDates, ...v.payDates].sort();
        const latestDate = allDates[allDates.length - 1] || null;
        const spotRate = rates[ccy] || 0;
        const days = daysUntil(latestDate);
        const volatility = getVolatility(days);
        return {
          currency: ccy, receivable: v.rec, payable: v.pay,
          naturalHedge, netExposure, direction,
          maturityDate: latestDate ? fromISO(latestDate) : "",
          maturityISO: latestDate, spotRate, volatility,
          budgetedRate: "", days,
        };
      }).filter(r => r.netExposure > 0);

      setExposures(rows);
      setHedgeRows(rows.map(r => ({ ...r, budgetedRate: "" })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingExposures(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (page === "hedge") loadExposures();
  }, [page, loadExposures]);

  const toggleProduct = (id) => {
    setAnswers(prev => {
      const current = prev.products_used || [];
      if (id === "none") return { ...prev, products_used: ["none"] };
      const without = current.filter(p => p !== "none");
      if (without.includes(id)) return { ...prev, products_used: without.filter(p => p !== id) };
      return { ...prev, products_used: [...without, id] };
    });
  };

  const validateAssessment = () => {
    if (answers.does_hedge === null) { alert("Please answer whether you hedge your exposure."); return false; }
    if (!answers.qualification) { alert("Please select your qualification."); return false; }
    if (answers.used_derivatives === null) { alert("Please answer whether you have used derivatives."); return false; }
    if (answers.lost_in_derivatives === null) { alert("Please answer whether you have lost in derivatives."); return false; }
    if (answers.board_allows_derivatives === null) { alert("Please answer whether your board allows derivatives."); return false; }
    return true;
  };

  const handleSubmitAssessment = async () => {
    const finalScore = calculateScore(answers);
    const grade = getGrade(finalScore);
    setScore(finalScore);
    setGradeResult(grade);
    setSaving(true);
    try {
      const { error } = await supabase
        .from('hedge_risk_assessments')
        .insert({
          company_id: companyId,
          does_hedge: answers.does_hedge,
          products_used: answers.products_used,
          qualification: answers.qualification,
          used_derivatives: answers.used_derivatives,
          lost_in_derivatives: answers.lost_in_derivatives,
          board_allows_derivatives: answers.board_allows_derivatives,
          grade: grade.grade,
          score: finalScore,
        });

      if (error) console.error("Insert error:", error);
      setExistingAssessment({ grade: grade.grade, score: finalScore });
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
    setPage("result");
  };

  const handleGetRecommendation = async (row) => {
    if (!row.budgetedRate) { alert("Please enter a budgeted rate."); return; }
    const budgeted = parseFloat(row.budgetedRate);
    if (budgeted < row.spotRate) {
      alert(`Budgeted rate (${budgeted}) cannot be less than today's spot rate (${row.spotRate}). Please enter a valid rate.`);
      return;
    }
    const rec = getRecommendation(gradeResult, row.volatility, row.days, budgeted, row.spotRate);
    try {
      const { error } = await supabase
        .from('hedge_recommendations')
        .insert({
          company_id: companyId,
          currency: row.currency,
          notional_amount: row.netExposure,
          maturity_date: toISO(row.maturityDate) || row.maturityISO,
          budgeted_rate: budgeted,
          fx_volatility: row.volatility,
          recommended_product: rec.product,
          indicative_rate: rec.indicativeRate,
          grade: gradeResult.grade,
        });

      if (error) console.error("Insert error:", error);
    } catch (e) {
      console.error(e);
    }
    setPopupData({ ...rec, row, gradeResult });
    setShowPopup(true);
  };

  const handleAddHedge = async () => {
    if (!addHedgeForm.contract_reference) { alert("Contract reference is required."); return; }
    if (!addHedgeForm.instrument_type) { alert("Please select an instrument type."); return; }
    if (!addHedgeForm.counterparty_bank) { alert("Please select a counterparty bank."); return; }
    if (!addHedgeForm.rate_locked_num) { alert("Please enter the rate locked."); return; }
    if (!addHedgeForm.maturity_date) { alert("Please enter maturity date."); return; }
    setAddHedgeSaving(true);
    try {
      const { error } = await supabase
        .from('hedge_positions')
        .insert({
          company_id: companyId,
          contract_reference: addHedgeForm.contract_reference,
          currency_pair: `${addHedgeRow.currency}/INR`,
          instrument_type: addHedgeForm.instrument_type,
          direction: addHedgeRow.direction === "Receivable" ? "Sell" : "Buy",
          notional_amount: (addHedgeForm.notional_amount || addHedgeRow.netExposure).toString(),
          notional_amount_num: parseFloat(addHedgeForm.notional_amount || addHedgeRow.netExposure),
          rate_locked_num: parseFloat(addHedgeForm.rate_locked_num),
          counterparty_bank: addHedgeForm.counterparty_bank,
          maturity_date: toISO(addHedgeForm.maturity_date),
          trade_date: new Date().toISOString().split("T")[0],
          settlement_type: addHedgeForm.settlement_type || "Gross Settlement",
        });

      if (error) {
        console.error("Insert error:", error);
        alert("Something went wrong. Please try again.");
      } else {
        alert("Hedge position added successfully! It will now reflect in Analytics → Details → Hedge Position.");
        setAddHedgeRow(null);
        setAddHedgeForm({});
      }
    } catch (e) {
      alert("Something went wrong. Please try again.");
    } finally {
      setAddHedgeSaving(false);
    }
  };

  if (page === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (page === "intro") {
    return (
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">Hedge Solutions</h2>
            <p className="text-gray-400 text-sm">Understanding FX risk and how to manage it</p>
          </div>
        </div>
        <div className="space-y-5">
          <div className="nebula-card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">What is Hedging?</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Your company may have receivables or payables in a foreign currency — and thus you are exposed to adverse movements of foreign exchange (FX).
                  <span className="text-indigo-400 font-medium"> Hedging enables you to protect your company from FX volatility</span> and gives you certainty in cash flow.
                  Without hedging, a sudden adverse move in exchange rates can significantly erode your profit margins or increase your cost of imports — even if your underlying business performs well.
                  Hedging is not speculation — it is a risk management tool that helps you plan with confidence.
                </p>
              </div>
            </div>
          </div>
          <div className="nebula-card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Forward Contracts</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  A Forward Contract helps you <span className="text-emerald-400 font-medium">lock in a fixed exchange rate for a time in the future</span>, matching your actual payment or receipt date.
                  The majority of companies use this instrument to protect their cash flows from currency fluctuations.
                  When this future rate locking happens through a bank (Over The Counter — OTC) and is tailored to your specific needs, it is called a <span className="text-white font-medium">Forward Contract</span>.
                  When it happens through an exchange with standardised contracts, it is called <span className="text-white font-medium">Futures</span>.
                </p>
              </div>
            </div>
          </div>
          <div className="nebula-card p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Derivatives & Structured Products</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Apart from Forwards and Futures, there are more complex products — collectively known as <span className="text-amber-400 font-medium">derivatives</span> — such as Options, Range Forwards, Seagulls, and Participating Forwards.
                  These are used by corporates with more sophisticated treasury functions.
                  However, <span className="text-red-400 font-medium">these products carry significantly higher risk</span> if not properly understood or managed.
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/20">
            <h3 className="text-base font-bold text-red-400 mb-3">⚠ Important Notice</h3>
            <p className="text-red-200/80 text-sm leading-relaxed mb-3">
              On the next page, we will conduct a brief risk assessment to understand your familiarity with hedging products.
            </p>
            <ul className="text-red-200/70 text-sm space-y-1.5 mb-3">
              <li>→ The person undertaking these hedging products must have sufficient knowledge and understanding of the instruments being used.</li>
              <li>→ The company must have adequate financial resources to cover any potential mark-to-market losses or margin calls.</li>
              <li>→ Appropriate board or management approvals must be in place before entering into any hedging transaction.</li>
              <li>→ The information provided is purely for informational purposes and does not constitute financial advice.</li>
            </ul>
            <p className="text-red-200/70 text-sm font-medium">Nebula Finance AI provides this tool as an educational and informational service only.</p>
          </div>
          <div className="flex justify-end">
            <button onClick={() => setPage("assessment")} className="nebula-btn-primary flex items-center gap-2 px-8 py-3 text-base">
              I Understand & Agree — Proceed to Risk Assessment
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (page === "assessment") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => setPage("intro")} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">Risk Assessment</h2>
            <p className="text-gray-400 text-sm">Answer honestly — this helps us recommend the right products for you</p>
          </div>
        </div>
        <div className="space-y-5">
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-4">1. Do you currently hedge your FX exposure?</p>
            <div className="flex gap-3">
              {[{ label: "Yes", value: true }, { label: "No", value: false }].map(opt => (
                <button key={opt.label} onClick={() => setAnswers(prev => ({ ...prev, does_hedge: opt.value }))}
                  className={`flex-1 py-3 rounded-xl border font-medium text-sm transition-all ${answers.does_hedge === opt.value ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0a0b0f] border-[#2a2d3a] text-gray-400 hover:border-indigo-500/50"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-1">2. What products do you use to hedge your exposure?</p>
            <p className="text-gray-500 text-xs mb-4">Select all that apply</p>
            <div className="space-y-2">
              {PRODUCTS.map(p => (
                <button key={p.id} onClick={() => toggleProduct(p.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border text-sm font-medium transition-all text-left ${(answers.products_used || []).includes(p.id) ? "bg-indigo-600/20 border-indigo-500 text-indigo-300" : "bg-[#0a0b0f] border-[#2a2d3a] text-gray-400 hover:border-indigo-500/50"}`}>
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${(answers.products_used || []).includes(p.id) ? "bg-indigo-600 border-indigo-500" : "border-[#2a2d3a]"}`}>
                    {(answers.products_used || []).includes(p.id) && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-4">3. What is your highest educational qualification?</p>
            <select value={answers.qualification} onChange={e => setAnswers(prev => ({ ...prev, qualification: e.target.value }))} className="nebula-input">
              <option value="">Select qualification...</option>
              {QUALIFICATIONS.map(q => <option key={q}>{q}</option>)}
            </select>
          </div>
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-2">4. Have you ever used derivatives for hedging?</p>
            <p className="text-gray-500 text-xs mb-4">Derivatives include Seagulls, Range Forwards, Participating Forwards etc.</p>
            <div className="flex gap-3">
              {[{ label: "Yes", value: true }, { label: "No", value: false }].map(opt => (
                <button key={opt.label} onClick={() => setAnswers(prev => ({ ...prev, used_derivatives: opt.value }))}
                  className={`flex-1 py-3 rounded-xl border font-medium text-sm transition-all ${answers.used_derivatives === opt.value ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0a0b0f] border-[#2a2d3a] text-gray-400 hover:border-indigo-500/50"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-2">5. Have you ever incurred a considerable loss in a derivative hedging contract?</p>
            <p className="text-gray-500 text-xs mb-4">This helps us understand your risk experience and resilience.</p>
            <div className="flex gap-3">
              {[{ label: "Yes", value: true }, { label: "No", value: false }].map(opt => (
                <button key={opt.label} onClick={() => setAnswers(prev => ({ ...prev, lost_in_derivatives: opt.value }))}
                  className={`flex-1 py-3 rounded-xl border font-medium text-sm transition-all ${answers.lost_in_derivatives === opt.value ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0a0b0f] border-[#2a2d3a] text-gray-400 hover:border-indigo-500/50"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="nebula-card p-6">
            <p className="text-white font-semibold mb-2">6. Does your Board of Directors permit the use of derivatives?</p>
            <p className="text-gray-500 text-xs mb-4">Board approval is a key governance requirement for derivative transactions.</p>
            <div className="flex gap-3">
              {[{ label: "Yes", value: true }, { label: "No", value: false }].map(opt => (
                <button key={opt.label} onClick={() => setAnswers(prev => ({ ...prev, board_allows_derivatives: opt.value }))}
                  className={`flex-1 py-3 rounded-xl border font-medium text-sm transition-all ${answers.board_allows_derivatives === opt.value ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0a0b0f] border-[#2a2d3a] text-gray-400 hover:border-indigo-500/50"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={() => { if (validateAssessment()) handleSubmitAssessment(); }} disabled={saving}
              className="nebula-btn-primary flex items-center gap-2 px-8 py-3 text-base">
              {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Submit Assessment <ChevronRight className="w-5 h-5" /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (page === "result" && gradeResult) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-white">Your Assessment Result</h2>
        </div>
        <div className={`nebula-card p-8 mb-6 border ${gradeResult.borderColor} text-center relative overflow-hidden`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${gradeResult.color} opacity-5`} />
          <div className="relative">
            <div className="text-6xl mb-4">{gradeResult.icon}</div>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1,2,3,4].map(s => (
                <Star key={s} className={`w-6 h-6 ${s <= gradeResult.stars ? gradeResult.textColor : "text-gray-700"}`} fill={s <= gradeResult.stars ? "currentColor" : "none"} />
              ))}
            </div>
            <div className={`inline-flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r ${gradeResult.color} mb-4`}>
              <Award className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-xl">{gradeResult.grade}</span>
            </div>
            <p className={`text-lg font-semibold ${gradeResult.textColor} mb-3`}>{gradeResult.message}</p>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xl mx-auto mb-4">{gradeResult.detail}</p>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${gradeResult.bg} border ${gradeResult.borderColor}`}>
              <span className="text-xs text-gray-400">Assessment Score:</span>
              <span className={`font-bold ${gradeResult.textColor}`}>{score} points</span>
            </div>
          </div>
        </div>
        <div className="nebula-card p-6 mb-5">
          <h3 className="text-lg font-bold text-white mb-3">Recommended Products for You</h3>
          <p className="text-gray-400 text-sm mb-4">{gradeResult.recommendation}</p>
          <div className="flex flex-wrap gap-2">
            {gradeResult.products.map((p, i) => (
              <span key={i} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${gradeResult.bg} ${gradeResult.textColor} ${gradeResult.borderColor}`}>
                ✓ {p}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => { setAnswers({ does_hedge: null, products_used: [], qualification: "", used_derivatives: null, lost_in_derivatives: null, board_allows_derivatives: null }); setPage("assessment"); }}
            className="px-6 py-2.5 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all"
          >
            Retake Assessment
          </button>
          <button onClick={() => setPage("hedge")} className="nebula-btn-primary px-6 py-2.5 text-sm flex items-center gap-2">
            Proceed to Hedge Planner <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (page === "hedge") {
    return (
      <div className="max-w-screen-xl mx-auto px-6 py-6">

        {/* Recommendation Popup */}
        {showPopup && popupData && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="nebula-card p-8 max-w-lg w-full relative">
              <button onClick={() => setShowPopup(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r ${popupData.gradeResult.color} mb-4`}>
                  <Award className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-semibold">{popupData.gradeResult.grade} Grade Recommendation</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">Recommended Product</h3>
                <div className={`text-3xl font-bold mt-3 mb-1 ${popupData.gradeResult.textColor}`}>
                  {popupData.product}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] text-center">
                  <p className="text-xs text-gray-500 mb-1">Currency</p>
                  <p className="text-white font-bold">{popupData.row.currency}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] text-center">
                  <p className="text-xs text-gray-500 mb-1">Net Exposure</p>
                  <p className="text-white font-bold">{popupData.row.netExposure.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] text-center">
                  <p className="text-xs text-gray-500 mb-1">Today's Spot Rate</p>
                  <p className="text-white font-bold">{popupData.row.spotRate}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <p className="text-xs text-gray-500 mb-1">Indicative Rate</p>
                  <p className="text-emerald-400 font-bold text-lg">{popupData.indicativeRate}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] text-center">
                  <p className="text-xs text-gray-500 mb-1">FX Volatility</p>
                  <p className="text-amber-400 font-bold">{popupData.row.volatility}%</p>
                </div>
                <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] text-center">
                  <p className="text-xs text-gray-500 mb-1">Maturity</p>
                  <p className="text-white font-bold">{popupData.row.maturityDate || "—"}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 mb-5">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Why this product?</p>
                <p className="text-indigo-200 text-sm leading-relaxed">{popupData.rationale}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-5">
                <p className="text-amber-300 text-xs">⚠ The indicative rate is for reference only and is subject to market conditions at the time of execution. Please contact your bank for a firm quote.</p>
              </div>
              <button onClick={() => setShowPopup(false)} className="nebula-btn-primary w-full py-3">Close</button>
            </div>
          </div>
        )}

        {/* Add Hedge Modal */}
        {addHedgeRow && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="nebula-card p-6 max-w-2xl w-full relative max-h-screen overflow-y-auto">
              <button onClick={() => { setAddHedgeRow(null); setAddHedgeForm({}); }} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-white mb-1">
                {addHedgeRow.alreadyHedged ? "Record Existing Hedge" : "Add New Hedge Position"}
              </h3>
              <p className="text-gray-400 text-sm mb-5">
                {addHedgeRow.alreadyHedged
                  ? "Record a hedge you have already done with your bank — it will reflect in your hedge positions."
                  : "Add a new hedge for this exposure — it will be saved to your hedge positions."}
              </p>
              <div className="grid grid-cols-3 gap-3 mb-5 p-4 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Currency Pair</p>
                  <p className="text-white font-bold">{addHedgeRow.currency}/INR</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Net Exposure</p>
                  <p className="text-white font-bold">{addHedgeRow.netExposure.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Direction</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${addHedgeRow.direction === "Receivable" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                    {addHedgeRow.direction === "Receivable" ? "Sell" : "Buy"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Contract Reference <span className="text-red-400">*</span></label>
                  <input value={addHedgeForm.contract_reference || ""} onChange={e => setAddHedgeForm(prev => ({ ...prev, contract_reference: e.target.value }))} className="nebula-input text-sm" placeholder="e.g. FWD-2026-001" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Instrument Type <span className="text-red-400">*</span></label>
                  <select value={addHedgeForm.instrument_type || ""} onChange={e => setAddHedgeForm(prev => ({ ...prev, instrument_type: e.target.value }))} className="nebula-input text-sm">
                    <option value="">Select...</option>
                    {(gradeResult?.products || ["Forward Contract"]).map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Notional Amount</label>
                  <input type="number" value={addHedgeForm.notional_amount || addHedgeRow.netExposure} onChange={e => setAddHedgeForm(prev => ({ ...prev, notional_amount: e.target.value }))} className="nebula-input text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Rate Locked <span className="text-red-400">*</span></label>
                  <input type="number" step="0.0001" value={addHedgeForm.rate_locked_num || ""} onChange={e => setAddHedgeForm(prev => ({ ...prev, rate_locked_num: e.target.value }))} className="nebula-input text-sm" placeholder={`Spot: ${addHedgeRow.spotRate}`} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Counterparty Bank <span className="text-red-400">*</span></label>
                  <select value={addHedgeForm.counterparty_bank || ""} onChange={e => setAddHedgeForm(prev => ({ ...prev, counterparty_bank: e.target.value }))} className="nebula-input text-sm">
                    <option value="">Select bank...</option>
                    {BANKS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Maturity Date (DD-MM-YYYY) <span className="text-red-400">*</span></label>
                  <input value={addHedgeForm.maturity_date || addHedgeRow.maturityDate || ""} onChange={e => setAddHedgeForm(prev => ({ ...prev, maturity_date: formatDateInput(e.target.value) }))} className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Settlement Type</label>
                  <select value={addHedgeForm.settlement_type || "Gross Settlement"} onChange={e => setAddHedgeForm(prev => ({ ...prev, settlement_type: e.target.value }))} className="nebula-input text-sm">
                    <option>Gross Settlement</option>
                    <option>Net Settlement</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={handleAddHedge} disabled={addHedgeSaving} className="nebula-btn-primary flex items-center gap-2 px-6 py-2.5 text-sm">
                  {addHedgeSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle className="w-4 h-4" /> Save Hedge Position</>}
                </button>
                <button onClick={() => { setAddHedgeRow(null); setAddHedgeForm({}); }} className="px-6 py-2.5 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-sm transition-all">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={onBack} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">Hedge Planner</h2>
            <p className="text-gray-400 text-sm">Net exposures after natural hedge · Enter budgeted rate to get recommendation</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {gradeResult && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${gradeResult.bg} border ${gradeResult.borderColor}`}>
                <span className="text-lg">{gradeResult.icon}</span>
                <span className={`text-sm font-semibold ${gradeResult.textColor}`}>{gradeResult.grade}</span>
              </div>
            )}
            <button onClick={loadExposures} className="p-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white transition-all">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setPage("intro")} className="px-3 py-2 rounded-xl border border-[#2a2d3a] text-gray-400 hover:text-white text-xs transition-all">
              Retake Assessment
            </button>
          </div>
        </div>

        {loadingExposures ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : hedgeRows.length === 0 ? (
          <div className="nebula-card p-12 text-center text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">No net exposures found</p>
            <p className="text-sm">Add receivables and payables in the Details tab to see your FX exposures here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {hedgeRows.map((row, i) => (
              <div key={i} className="nebula-card p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{row.currency}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{row.currency} Exposure</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${row.direction === "Receivable" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                      Net {row.direction}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">
                  <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <p className="text-xs text-gray-500 mb-1">Receivables</p>
                    <p className="text-emerald-400 font-semibold">{row.receivable.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <p className="text-xs text-gray-500 mb-1">Payables</p>
                    <p className="text-red-400 font-semibold">{row.payable.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
                    <p className="text-xs text-gray-500 mb-1">Natural Hedge</p>
                    <p className="text-teal-400 font-semibold">{row.naturalHedge.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <p className="text-xs text-gray-500 mb-1">Net Exposure</p>
                    <p className="text-white font-bold">{row.netExposure.toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <p className="text-xs text-gray-500 mb-1">Today's Spot</p>
                    <p className="text-white font-semibold">{row.spotRate || "N/A"}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-gray-500 mb-1">FX Volatility</p>
                    <p className="text-amber-400 font-semibold">{row.volatility}%</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Maturity Date (DD-MM-YYYY)</label>
                    <input
                      value={row.maturityDate}
                      onChange={e => {
                        const val = formatDateInput(e.target.value);
                        const updated = [...hedgeRows];
                        const isoDate = val.length === 10 ? toISO(val) : null;
                        const days = isoDate ? daysUntil(isoDate) : null;
                        updated[i] = { ...updated[i], maturityDate: val, maturityISO: isoDate, days, volatility: getVolatility(days) };
                        setHedgeRows(updated);
                      }}
                      className="nebula-input text-sm" placeholder="DD-MM-YYYY" maxLength={10}
                    />
                    {row.days !== null && row.days >= 0 && (
                      <p className="text-xs text-gray-500 mt-1">{row.days} days until maturity</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Budgeted Rate <span className="text-red-400">*</span>
                      <span className="text-gray-600 ml-1">(min: {row.spotRate})</span>
                    </label>
                    <input
                      type="number" step="0.0001"
                      value={row.budgetedRate}
                      onChange={e => {
                        const updated = [...hedgeRows];
                        updated[i] = { ...updated[i], budgetedRate: e.target.value };
                        setHedgeRows(updated);
                      }}
                      className={`nebula-input text-sm ${row.budgetedRate && parseFloat(row.budgetedRate) < row.spotRate ? "border-red-500" : ""}`}
                      placeholder={`e.g. ${row.spotRate ? (row.spotRate * 1.02).toFixed(2) : "85.00"}`}
                    />
                    {row.budgetedRate && parseFloat(row.budgetedRate) < row.spotRate && (
                      <p className="text-xs text-red-400 mt-1">⚠ Rate cannot be less than today's spot rate ({row.spotRate})</p>
                    )}
                  </div>
                  <div className="md:col-span-2 grid grid-cols-3 gap-3">
                    <button onClick={() => handleGetRecommendation(row)} className="nebula-btn-primary flex items-center justify-center gap-2 py-3 text-sm">
                      <Shield className="w-4 h-4" /> Get Recommendation
                    </button>
                    <button onClick={() => { setAddHedgeRow({ ...row, index: i }); setAddHedgeForm({ maturity_date: row.maturityDate, notional_amount: row.netExposure }); }}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 transition-all text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> Add Hedge
                    </button>
                    <button onClick={() => { setAddHedgeRow({ ...row, index: i, alreadyHedged: true }); setAddHedgeForm({ maturity_date: row.maturityDate, notional_amount: row.netExposure }); }}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl border border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 transition-all text-sm font-medium">
                      <TrendingUp className="w-4 h-4" /> Already Hedged
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
